import { useCallback } from 'react';

import {
  ExternalPayoffIntelligenceFieldsInput,
  LienholderRequirement,
  useExternalAcquirePayoffForDealMutation,
} from '../../../../../gql/generated/graphql';
import { trackPayoffCardSubmitTapped } from '../../../../../services/analytics/marketplaceEvents';
import { logger } from '../../../../../services/sentry';
import { PayoffMissingFieldFormValues } from '../../../../LoanApp/components/PayoffIntelligenceMissingFields';

const formatDateOnly = (value: unknown): string | undefined => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  if (typeof value === 'string' && value.length > 0) return value;
  return undefined;
};

const optionalString = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.length > 0) return value;
  return undefined;
};

const buildFieldsInput = (
  values: PayoffMissingFieldFormValues,
): ExternalPayoffIntelligenceFieldsInput => ({
  ssn: optionalString(values.ssn),
  dob: formatDateOnly(values.dob),
  first_name: optionalString(values.first_name),
  last_name: optionalString(values.last_name),
  phone_number: optionalString(values.phone_number),
  address_line: optionalString(values.address_line),
  city: optionalString(values.city),
  state: optionalString(values.state),
  zip: optionalString(values.zip),
});

type UsePayoffMissingFieldsSubmitArgs = {
  dealId: string;
  missingFields: LienholderRequirement[];
  requiredFields: LienholderRequirement[];
};

const usePayoffMissingFieldsSubmit = ({
  dealId,
  missingFields,
  requiredFields,
}: UsePayoffMissingFieldsSubmitArgs) => {
  const [externalAcquirePayoff] = useExternalAcquirePayoffForDealMutation();

  return useCallback(
    async (values: PayoffMissingFieldFormValues): Promise<boolean> => {
      trackPayoffCardSubmitTapped(dealId, missingFields);
      const fields = buildFieldsInput(values);
      try {
        await externalAcquirePayoff({
          variables: { dealId, fields, payloadFields: requiredFields },
        });
        return true;
      } catch (err) {
        logger.error(
          'usePayoffMissingFieldsSubmit',
          'externalAcquirePayoffForDeal failed',
          '',
          err,
        );
        return false;
      }
    },
    [dealId, missingFields, requiredFields, externalAcquirePayoff],
  );
};

export default usePayoffMissingFieldsSubmit;
